import React, { memo, useMemo } from 'react';
import cn from 'classnames';
import { useRecoilValue } from 'recoil';
import useSWR from 'swr';

import { Icon } from 'components';
import { Col, Row } from 'components/layout';
import { ButtonV2 } from 'components/form';
import { useCommon } from 'hooks/useCommon';
import { useNotification } from 'hooks/useNotification';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { get } from 'lib/utils/http';

import { API_ROUTES } from 'lib/api-routes';
import { parseArrayResponse } from 'lib/utils/parser';
import {
  SocietyManagersResponse,
  SocietyManagerAttributes,
} from 'lib/models/student-society-manager';
import styles from './InviteCommitteeCard.module.scss';

export interface InviteCommitteeCardProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  // Props to be used if need to show this as Toaster
  isToaster?: boolean;
  onCloseToaster?: () => void;
}

export const InviteCommitteeCard: React.FC<InviteCommitteeCardProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: InviteCommitteeCardProps) => {
    const contentClassNames = cn({
      [styles.InviteCommitteeCard]: true,
    });

    const { headers } = useRecoilValue(httpHeadersState);

    const { data: pendingApprovalResponse, mutate: mutateList } =
      useSWR<SocietyManagersResponse>(
        [API_ROUTES.SOCIETY_MANAGERS, headers],
        get,
        { revalidateOnFocus: false }
      );
    const pendingApprovalList = useMemo(() => {
      return parseArrayResponse<SocietyManagerAttributes>(
        pendingApprovalResponse
      );
    }, [pendingApprovalResponse]);

    const { onApproveMember, onRejectMember } = useCommon();

    const notificationInstance = useNotification();
    const onAcceptInvite = async (id: string, email: string) => {
      await onApproveMember({ headers, id });
      mutateList();
      notificationInstance.success({
        title: 'Approved',
        message: `${email} has been approved as a Committee member.`,
      });
    };
    const onDeclineInvite = async (id: string) => {
      await onRejectMember({ headers, id });
      mutateList();
      notificationInstance.success({
        message: 'Declined',
      });
    };

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="InviteCommitteeCard"
      >
        {pendingApprovalList?.length > 0
          ? pendingApprovalList.map(({ id, email }) => (
              <div key={id} className={cn(styles.cohosting, className)}>
                <Row align="center">
                  <Col className={styles.cohostingInfo}>
                    <Row className={styles.cohostingHeader}>
                      <Col>
                        <Icon iconName="warning" size="large" />
                      </Col>
                      <Col>
                        <p className={cn(styles.info, styles.mainTitle)}>
                          Approve Committee member
                        </p>
                        <p>
                          <mark>{email}</mark> has requested to join your
                          society as a Committee member.
                        </p>
                      </Col>
                    </Row>
                  </Col>
                  <Col className={styles.buttons}>
                    <ButtonV2
                      onClick={() => {
                        onAcceptInvite(id, email);
                      }}
                      className={styles.cohostingButton}
                    >
                      Accept
                    </ButtonV2>
                    <ButtonV2
                      onClick={() => {
                        onDeclineInvite(id);
                      }}
                      className={styles.cohostingButton}
                    >
                      Decline
                    </ButtonV2>
                  </Col>
                </Row>
              </div>
            ))
          : null}
      </div>
    );
  }
);

InviteCommitteeCard.displayName = 'InviteCommitteeCard';
